import { useEffect, useState } from "react";
import { useNextT } from "../i18n";
import type { SignInFailure } from "./signInErrors";
import s from "./public.module.scss";

/** Whole seconds left, counting down once a second from `from`; 0 means done. Starting again with a new `from` restarts it. */
export const useCountdown = (from: number, restartKey: unknown = from): number => {
  const [left, setLeft] = useState(from);
  useEffect(() => { setLeft(from); }, [from, restartKey]);
  useEffect(() => {
    if (left <= 0) return;
    const id = setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [left]);
  return left;
};

/** P2.3: the sign-in failure in our own words, on the form; "too many tries" counts down live and clears itself at zero. */
export const SignInErrorLine = ({ failure, onDone }: { failure: SignInFailure | null; onDone: () => void }) => {
  const { t } = useNextT();
  const total = failure?.kind === "throttle" ? failure.seconds : 0;
  const left = useCountdown(total, failure);
  useEffect(() => { if (failure?.kind === "throttle" && left <= 0) onDone(); }, [failure, left, onDone]);
  if (!failure) return null;
  if (failure.kind === "throttle" && left <= 0) return null;
  if (failure.kind !== "mismatch" && failure.kind !== "throttle") return null; // a page of its own (the sign-in page shows it)
  return (
    <p className="authshell-field__error" role="alert">
      {failure.kind === "mismatch" ? t("public.signin.mismatch") : t("public.signin.throttle", { seconds: left })}
    </p>
  );
};

/** True while the sign-in button must wait: the lockout is still counting. */
export const isLockedOut = (failure: SignInFailure | null): boolean => failure?.kind === "throttle";

/** P3.1: the used / expired one-time link, as a page in the frame. The address it was meant for is never shown. */
export const LinkProblem = ({ onPassword, onAnother }: { onPassword: () => void; onAnother: () => void }) => {
  const { t } = useNextT();
  return (
    <div className="authshell-stack">
      <p className={s.linkNote}>{t("public.link.sent_new")}</p>
      <button type="button" className="authshell-cta" onClick={onPassword}>{t("public.link.with_password")}</button>
      <button type="button" className="authshell-cta authshell-cta--outline" onClick={onAnother}>{t("public.link.send_another")}</button>
    </div>
  );
};

/** P3.4: after a sign-in link was asked for: the same words for every address, what to do if it does not come, "Send it again" after 30 seconds. */
export const LinkSent = ({ onAgain, onPassword, restartKey }: { onAgain: () => void; onPassword: () => void; restartKey: number }) => {
  const { t } = useNextT();
  const left = useCountdown(30, restartKey);
  return (
    <div className="authshell-stack">
      <p className={s.linkNote} role="status">{t("public.link.sent_body")}</p>
      <button type="button" className="authshell-cta" disabled={left > 0} onClick={onAgain}>
        {left > 0 ? t("public.link.send_again_in", { seconds: left }) : t("public.link.send_again")}
      </button>
      <button type="button" className="authshell-cta authshell-cta--outline" onClick={onPassword}>{t("public.link.with_password")}</button>
    </div>
  );
};

/** P3.3: someone is signed in and opens a sign-in link meant for another person. */
export const SwitchAccount = ({ name, other, busy, onContinue, onStay }: { name: string; other: string; busy: boolean; onContinue: () => void; onStay: () => void }) => {
  const { t } = useNextT();
  return (
    <div className="authshell-stack">
      <button type="button" className="authshell-cta" disabled={busy} onClick={onContinue}>{t("public.switch.continue", { other })}</button>
      <button type="button" className="authshell-cta authshell-cta--outline" disabled={busy} onClick={onStay}>{t("public.switch.stay", { name })}</button>
    </div>
  );
};

/** The heading the switch page shows (the sentence of P3.3 with both names). */
export const useSwitchTitle = (name: string, other: string): string => {
  const { t } = useNextT();
  return t("public.switch.title", { name, other });
};
